import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';
import Header from './components/Header';
import ControlPanel from './components/ControlPanel';
import Dashboard from './components/Dashboard';
import AccountsModal from './components/AccountsModal';
import BackToTop from './components/BackToTop';
import Toast from './components/Toast';
import { ToastProvider, useToast } from './context/ToastContext';
import axios from 'axios';
import { calculateLinearRegression } from './utils/trendCalculator';

function AppContent() {
  const { showToast } = useToast();
  const [accounts, setAccounts] = useState([]);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [groups, setGroups] = useState([]);
  const [period, setPeriod] = useState('30d');
  const [customDates, setCustomDates] = useState({ start: '', end: '' });
  const [compareMode, setCompareMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAccountsModal, setShowAccountsModal] = useState(false);
  const [sortBy, setSortBy] = useState('users-desc');
  const [activeGroup, setActiveGroup] = useState(null);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/accounts');
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const email = accounts.length > 0 ? accounts[0].email : '';
      if (email) {
        const response = await axios.get(`/api/groups?email=${email}`);
        setGroups(response.data);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        period,
        compare: compareMode,
        ...(period === 'custom' && {
          customStart: customDates.start,
          customEnd: customDates.end
        })
      };

      const response = await axios.get('/api/analytics', { params });
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      showToast('Error loading analytics data. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [period, customDates, compareMode]);

  // Fetch accounts on mount
  useEffect(() => {
    fetchAccounts();

    // Check for OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth') === 'success') {
      const newCount = parseInt(params.get('new')) || 0;
      const updatedCount = parseInt(params.get('updated')) || 0;

      let message = '';
      if (newCount > 0 && updatedCount > 0) {
        message = `Connected ${newCount} new account${newCount > 1 ? 's' : ''} and updated ${updatedCount} existing account${updatedCount > 1 ? 's' : ''}`;
      } else if (newCount > 0) {
        message = `Successfully connected ${newCount} account${newCount > 1 ? 's' : ''}`;
      } else if (updatedCount > 0) {
        message = `Updated ${updatedCount} existing account${updatedCount > 1 ? 's' : ''}`;
      } else {
        message = 'Your Google Analytics accounts have been synchronized';
      }

      showToast(message, 'success');
      window.history.replaceState({}, document.title, '/');
      fetchAccounts();
    } else if (params.get('auth') === 'error') {
      showToast(`Authentication error: ${params.get('message')}`, 'error');
      window.history.replaceState({}, document.title, '/');
    }
  }, [showToast]);

  // Fetch groups when accounts are loaded
  useEffect(() => {
    if (accounts.length > 0) {
      fetchGroups();
    }
  }, [accounts]);

  // Fetch analytics data when period or compare mode changes
  useEffect(() => {
    if (accounts.length > 0) {
      fetchAnalyticsData();
    }
  }, [period, customDates, compareMode, accounts, fetchAnalyticsData]);

  const handleConnectAccount = async (email = null) => {
    try {
      const params = email ? { email } : {};
      const response = await axios.get('/api/auth/google', { params });
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Error connecting account:', error);
      showToast('Error initiating Google authentication', 'error');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (window.confirm('Are you sure you want to remove this account?')) {
      try {
        await axios.delete(`/api/accounts/${accountId}`);
        fetchAccounts();
        showToast('Account removed successfully', 'success');
      } catch (error) {
        console.error('Error deleting account:', error);
        showToast('Error removing account', 'error');
      }
    }
  };

  const handlePeriodChange = (newPeriod, customStart, customEnd) => {
    setPeriod(newPeriod);
    if (newPeriod === 'custom') {
      setCustomDates({ start: customStart, end: customEnd });
    }
  };

  // Enrich analytics data with trend calculations
  const analyticsWithTrend = useMemo(() => {
    return analyticsData.map(account => {
      if (account.error || !account.data || account.data.length < 2) {
        return account;
      }
      const trend = calculateLinearRegression(account.data);
      return { ...account, trend };
    });
  }, [analyticsData]);

  // Sort analytics data based on selected sort option
  const sortedAnalyticsData = useMemo(() => {
    const sortedData = [...analyticsWithTrend];

    switch (sortBy) {
      case 'name':
        return sortedData.sort((a, b) => {
          const nameA = a.websiteName || a.websiteUrl || a.propertyName || '';
          const nameB = b.websiteName || b.websiteUrl || b.propertyName || '';
          return nameA.localeCompare(nameB);
        });

      case 'users-asc':
        return sortedData.sort((a, b) => (a.totalUsers || 0) - (b.totalUsers || 0));

      case 'users-desc':
        return sortedData.sort((a, b) => (b.totalUsers || 0) - (a.totalUsers || 0));

      case 'trend':
        return sortedData.sort((a, b) => {
          const getAbsoluteChange = (account) => {
            if (!account.data || account.data.length < 2) return 0;
            const first = account.data[0]?.users || 0;
            const last = account.data[account.data.length - 1]?.users || 0;
            return Math.abs(last - first);
          };

          return getAbsoluteChange(b) - getAbsoluteChange(a);
        });

      default:
        return sortedData;
    }
  }, [analyticsWithTrend, sortBy]);

  // Calculate ungrouped accounts count independently
  const ungroupedAccountsCount = useMemo(() => {
    return sortedAnalyticsData.filter(account => {
      return !account.groupId ||
        account.groupId === null ||
        account.groupId === undefined ||
        account.groupId === '' ||
        (account.groupId && !account.groupId._id);
    }).length;
  }, [sortedAnalyticsData]);

  // Filter analytics data by selected group
  const filteredAnalyticsData = useMemo(() => {
    if (activeGroup === null) {
      return sortedAnalyticsData;
    }
    return sortedAnalyticsData.filter(account => {
      if (activeGroup === 'ungrouped') {
        return !account.groupId ||
          account.groupId === null ||
          account.groupId === undefined ||
          account.groupId === '' ||
          (account.groupId && !account.groupId._id);
      }
      return account.groupId && account.groupId._id === activeGroup;
    });
  }, [sortedAnalyticsData, activeGroup]);

  return (
    <div className="App">
      <Toast />
      <Header
        onManageAccounts={() => setShowAccountsModal(true)}
        accountsCount={accounts.length}
      />

      <ControlPanel
        period={period}
        compareMode={compareMode}
        onPeriodChange={handlePeriodChange}
        onCompareModeChange={setCompareMode}
        sortBy={sortBy}
        onSortChange={setSortBy}
        groups={groups}
        activeGroup={activeGroup}
        onGroupChange={setActiveGroup}
        accountsCount={analyticsData.length}
        ungroupedAccountsCount={ungroupedAccountsCount}
        showGroups={groups.length > 0}
        showSort={analyticsData.length > 0}
      />

      <div className="main-content">
        <Dashboard
          analyticsData={filteredAnalyticsData}
          loading={loading}
          compareMode={compareMode}
          onReconnect={handleConnectAccount}
        />
      </div>

      {showAccountsModal && (
        <AccountsModal
          accounts={accounts}
          onClose={() => setShowAccountsModal(false)}
          onDelete={handleDeleteAccount}
          onRefresh={fetchAccounts}
          onConnect={handleConnectAccount}
          email={accounts.length > 0 ? accounts[0].email : ''}
        />
      )}

      <BackToTop />
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}

export default App;
